import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { BookOpen, CloudDownload, LifeBuoy, RefreshCcw, Route, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { AppFlowViewer } from "../../components/appflow/AppFlowViewer";   // Phase 222
import { Spinner } from "../../components/common";
import { ManualReader } from "../../components/manual/ManualReader";
import { errMsg } from "../../lib/api";
import { downloadManual, getCachedManual, useOnline } from "../../lib/offline";

const fmtWhen = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
};

/**
 * Collector "Help & Manual" — offline-first.
 * 1. Show the copy saved on this device immediately (if any).
 * 2. When online, fetch the latest role-filtered manual, refresh the device copy.
 * 3. Offline: search + Markdown export run on-device; PDF export waits for network.
 */
export default function CollectorHelp() {
  const { code } = useParams();
  const online = useOnline();
  const [data, setData] = useState(null);       // cached or fresh manual doc
  const [source, setSource] = useState(null);   // "device" | "server"
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [params, setParams] = useSearchParams();   // Phase 222: ?tab=appflow opens the App Flow tab
  const tab = params.get("tab") === "appflow" ? "appflow" : "manual";
  const setTab = (v) => { const p = new URLSearchParams(params); if (v === "appflow") p.set("tab", "appflow"); else p.delete("tab"); setParams(p, { replace: true }); };

  const refresh = useCallback(async (silent = false) => {
    if (!navigator.onLine) { if (!silent) toast.info("You are offline — showing the copy saved on this device"); return; }
    setRefreshing(true);
    try {
      const fresh = await downloadManual(code);
      setData(fresh); setSource("server");
      if (!silent) toast.success("Manual updated and saved on this device");
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
    } finally { setRefreshing(false); }
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await getCachedManual(code);
      if (cancelled) return;
      if (cached) { setData(cached); setSource("device"); }
      setLoading(false);
      await refresh(true);
    })();
    return () => { cancelled = true; };
  }, [code, refresh]);

  if (loading) return <Spinner label="Opening manual..." />;

  if (!data && tab === "appflow") {
    // the manual copy is not on the device yet, but the App Flow (online) must still open
    return (
      <div className="space-y-3" data-testid="collector-help">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="manual" className="flex-1" data-testid="help-tab-manual"><BookOpen className="h-4 w-4 mr-1.5" /> Manual</TabsTrigger>
            <TabsTrigger value="appflow" className="flex-1" data-testid="help-tab-appflow"><Route className="h-4 w-4 mr-1.5" /> App Flow</TabsTrigger>
          </TabsList>
        </Tabs>
        <AppFlowViewer fetchPath={`/t/${code}/app-flow`} exportPath={`/t/${code}/app-flow/export.pptx`} compact />
      </div>
    );
  }
  if (!data) {
    return (
      <Card className="shadow-sm" data-testid="collector-help-empty">
        <CardContent className="p-6 text-center space-y-3">
          <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
            {online ? <CloudDownload className="h-6 w-6 text-muted-foreground" /> : <WifiOff className="h-6 w-6 text-muted-foreground" />}
          </div>
          <p className="font-medium">{online ? "Manual not downloaded yet" : "No manual saved on this device"}</p>
          <p className="text-sm text-muted-foreground">
            {online ? "Tap below to download the guide. It is saved on this phone so you can read it offline in the field."
                    : "Connect to the internet once and open this page — the manual is then saved on this phone for offline use."}
          </p>
          <Button onClick={() => refresh(false)} disabled={!online || refreshing} data-testid="collector-help-download">
            <CloudDownload className="h-4 w-4 mr-1.5" /> {refreshing ? "Downloading..." : "Download manual"}
          </Button>
          <Button variant="outline" onClick={() => setTab("appflow")} disabled={!online} data-testid="collector-help-open-appflow">
            <Route className="h-4 w-4 mr-1.5" /> Open App Flow
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="collector-help">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold font-heading flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-primary" /> Help & Manual</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Field guide for collectors and the citizen portal — works without network</p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0 h-8" onClick={() => refresh(false)} disabled={!online || refreshing} data-testid="collector-help-refresh">
          <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Updating" : "Update"}
        </Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="manual" className="flex-1" data-testid="help-tab-manual"><BookOpen className="h-4 w-4 mr-1.5" /> Manual</TabsTrigger>
          <TabsTrigger value="appflow" className="flex-1" data-testid="help-tab-appflow"><Route className="h-4 w-4 mr-1.5" /> App Flow</TabsTrigger>
        </TabsList>
        <TabsContent value="manual" className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-[11px]" data-testid="collector-help-version">
              Manual v{data.version?.number} · {data.version?.release_tag}
            </Badge>
            <Badge variant="outline" className={`text-[11px] ${online ? "" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="collector-help-offline-status">
              {online ? "Saved for offline use" : "Offline — reading saved copy"}{data.cached_at ? ` · ${fmtWhen(data.cached_at)}` : ""}
            </Badge>
            {source === "device" && online && refreshing && <span className="text-[11px] text-muted-foreground" data-testid="collector-help-source">checking for updates…</span>}
          </div>
          <ManualReader
            guides={data.guides}
            versionMeta={data.version}
            searchPath={`/t/${code}/manual/search`}
            exportPath={`/t/${code}/manual/export`}
            initialGuide="tax_collector"
            offline={!online}
            compact
          />
        </TabsContent>
        <TabsContent value="appflow" className="mt-3">
          <AppFlowViewer fetchPath={`/t/${code}/app-flow`} exportPath={`/t/${code}/app-flow/export.pptx`} compact />
        </TabsContent>
      </Tabs>
    </div>
  );
}
